import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { 
  Bell, Save, CheckCircle, AlertCircle, Info, 
  Clock, Calendar, Globe, Loader2 
} from 'lucide-react';
import NotificationFirebaseService from './NotificationFirebaseService';
import './NotificationSettings.css';

const NotificationSettings = ({ userId }) => {
  const [settings, setSettings] = useState({
    enabled: true,
    preferredTime: '09:00',
    timezone: 'Africa/Casablanca'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    if (!currentUserId) {
      setLoading(false);
      return;
    }

    try {
      const userSettings = await NotificationFirebaseService.getNotificationSettings(currentUserId);
      setSettings(userSettings);
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
      showMessage('error', 'Erreur lors du chargement des paramètres');
    }
    
    setLoading(false);
  };

  const saveSettings = async () => {
    if (!currentUserId) {
      showMessage('error', 'Erreur d\'authentification');
      return;
    }
    
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      await NotificationFirebaseService.updateNotificationSettings(currentUserId, settings);
      showMessage('success', '✓ Paramètres sauvegardés avec succès !');
    } catch (error) {
      console.error('Erreur sauvegarde paramètres:', error);
      showMessage('error', 'Erreur lors de la sauvegarde des paramètres');
    }
    
    setSaving(false);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => {
      setMessage({ type: '', text: '' });
    }, 4000);
  };

  if (loading) {
    return (
      <div className="notification-settings">
        <div className="loading-state">
          <Loader2 className="spinner-icon" size={40} />
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-settings">
      {/* Message de feedback */}
      {message.text && (
        <div className={`alert-message alert-${message.type}`}>
          <div className="alert-content">
            {message.type === 'success' ? (
              <CheckCircle size={20} />
            ) : message.type === 'info' ? (
              <Info size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span>{message.text}</span>
          </div>
          <button 
            onClick={() => setMessage({ type: '', text: '' })}
            className="alert-close"
          >
            ×
          </button>
        </div>
      )}

      {/* Formulaire de paramètres */}
      <div className="settings-form">
        {/* Activation/Désactivation */}
        <div className="setting-row">
          <div className="setting-info">
            <div className="setting-label-row">
              <Bell size={20} className="setting-icon" />
              <label className="setting-label">Recevoir des notifications</label>
            </div>
            <p className="setting-description">
              Recevez des suggestions personnalisées pour faire avancer vos objectifs
            </p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        {settings.enabled && (
          <>
            <div className="settings-divider"></div>

            {/* Heure préférée */}
            <div className="setting-row vertical">
              <div className="setting-info">
                <div className="setting-label-row">
                  <Clock size={20} className="setting-icon" />
                  <label className="setting-label">Heure préférée</label>
                </div>
                <p className="setting-description">
                  À quel moment préférez-vous recevoir vos notifications ?
                </p>
              </div>
              <select
                value={settings.preferredTime}
                onChange={(e) => setSettings({ ...settings, preferredTime: e.target.value })}
                className="select-input"
              >
                <option value="09:00">🌅 9h00 - Début de matinée</option>
                <option value="12:00">☀️ 12h00 - Midi</option>
                <option value="14:00">🌤️ 14h00 - Début d'après-midi</option>
                <option value="18:00">🌆 18h00 - Fin de journée</option>
                <option value="20:00">🌙 20h00 - Soirée</option>
              </select>
            </div>

            <div className="settings-divider"></div>

            {/* Timezone */}
            <div className="setting-row vertical">
              <div className="setting-info">
                <div className="setting-label-row">
                  <Globe size={20} className="setting-icon" />
                  <label className="setting-label">Fuseau horaire</label>
                </div>
                <p className="setting-description">
                  Assurez-vous que l'heure correspond à votre localisation
                </p>
              </div>
              <select
                value={settings.timezone}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                className="select-input"
              >
                <option value="Africa/Casablanca">🇲🇦 Casablanca (GMT+1)</option>
                <option value="Europe/Paris">🇫🇷 Paris (GMT+1)</option>
                <option value="Europe/London">🇬🇧 Londres (GMT+0)</option>
                <option value="America/New_York">🇺🇸 New York (GMT-5)</option>
                <option value="America/Los_Angeles">🇺🇸 Los Angeles (GMT-8)</option>
                <option value="Asia/Dubai">🇦🇪 Dubai (GMT+4)</option>
              </select>
            </div>
          </>
        )}

        {/* Bouton de sauvegarde */}
        <div className="settings-actions">
          <button 
            onClick={saveSettings}
            disabled={saving}
            className="save-btn"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="spinner-icon" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={18} />
                Enregistrer les paramètres
              </>
            )}
          </button>
        </div>
      </div>

      {/* Bloc d'explication */}
      <div className="info-explanation-box">
        <div className="info-header">
          <Info size={22} className="info-icon-explanation" />
          <h3>Comment fonctionnent les notifications ?</h3>
        </div>
        <div className="info-content">
          <div className="info-step">
            <div className="step-number">1</div>
            <div className="step-text">
              <strong>Analyse automatique de votre progression</strong>
              <p>Aladinn analyse régulièrement l'avancement de vos objectifs pour identifier vos besoins</p>
            </div>
          </div>
          <div className="info-step">
            <div className="step-number">2</div>
            <div className="step-text">
              <strong>Notifications intelligentes et adaptées</strong>
              <p>Vous recevez des emails au moment optimal selon votre activité et vos besoins. La fréquence s'adapte automatiquement à votre engagement</p>
            </div>
          </div>
          <div className="info-step">
            <div className="step-number">4</div>
            <div className="step-text">
              <strong>Recommandations et suivi automatique</strong>
              <p>Recevez des suggestions personnalisées adaptées à votre contexte. Vos réponses enrichissent automatiquement vos notes et votre progression</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
